// reference: https://github.com/kentcdodds/bookshelf/blob/main/src/context/auth-context.js
'use client';

import { deleteCookie, getCookie } from '@/services/CookieHelper';
import { useRouter } from "next/navigation";
import { useState, useEffect, createContext, useContext, useMemo, useCallback } from 'react';
import { getIsTokenValid } from './actions';

// type ProvideAuthContextSetStateTuple = [boolean, React.Dispatch<React.SetStateAction<boolean>>];
interface ProvideAuthContext {
  logout: () => void,
  login: () => void,
  authenticated: boolean
}
const AuthContext = createContext<ProvideAuthContext | undefined>(undefined);

function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [authenticated, setAuthentication] = useState(false);

  useEffect(() => {
    // Check the cookie when the component is mounted
    async function fetchIsTokenValid() {
      const token = getCookie("token_access");
      const tokenIsValid = await getIsTokenValid(token);
      const auth = tokenIsValid;
      setAuthentication(auth);
    }
    fetchIsTokenValid();
  }, []);
  
  const login = useCallback(() => {
    setAuthentication(true);
    router.replace("/profile");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = useCallback(() => {
    router.replace("/login");
    deleteCookie("token_access");
    deleteCookie("token_refresh");
    setAuthentication(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({authenticated, logout, login}),
    [authenticated, logout, login],
  )
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export {
  AuthProvider,
  useAuth
}